import React from 'react';
import { useTrackableLink } from '@noths/polaris-client-react-hooks';
import {
  IconBasketOutlined,
  IconBasketSolid,
  IconHeartOutlined,
  IconHeartSolid,
  IconMenu,
  IconSearch,
} from '@noths/polaris-client-ribbons-design-system';
import { ICON_BUTTON_TEXT_CLASS } from '@noths/polaris-client-styles';
import { palette } from '@noths/polaris-design-system-theme-brand2026customer';

import { IconBarItem, IconBarItemLabel } from './IconBarItem';
import { MyAccount } from './MyAccount';
import * as styles from './IconBar.styles';
import { BASKET_PAGE_HREF, FAVOURITES_PAGE_HREF } from 'client-site-chrome-ui/constants/hrefs';
import { useSiteChromeDynamicContext } from 'client-site-chrome-ui/context/SiteChromeDynamicContext/useSiteChromeDynamicContext';
import { useUserContext } from 'client-site-chrome-ui/context/UserContext/useUserContext';

const BasketItemsCount = ({ count }: { count: number }) => (
  <span css={styles.basketCount}>
    <span data-testid="basket-items-count">{count}</span>
  </span>
);

const baseTrackingEvent = {
  event: 'custom_event',
  event_category: 'Header',
};
const favoritesClickTrackingEvent = {
  ...baseTrackingEvent,
  event_action: 'favourites click',
  event_label: 'Favourites',
};
const basketClickTrackingEvent = {
  ...baseTrackingEvent,
  event_action: 'basket click',
  event_label: 'Basket',
};

export interface IconBarProps {
  compact: boolean;
  onSearchIconClick: () => void;
  searchIconActive: boolean;
}

export const IconBar = ({ compact, onSearchIconClick, searchIconActive }: IconBarProps) => {
  const { basketItemsCount, hasFavouriteProducts } = useUserContext();
  const { navigationModal } = useSiteChromeDynamicContext();
  const { trackLinkClick, trackLinkMouseDown } = useTrackableLink();

  return (
    <ul css={styles.iconbar}>
      {compact && (
        <IconBarItem visibleBelowBreakpoint="M">
          <button
            css={styles.iconBtn}
            data-testid="button-icon-search"
            onClick={onSearchIconClick}
            type="button"
          >
            <IconSearch
              color={searchIconActive ? palette.purpleBase : palette.deepPurpleBase}
              css={styles.icon}
            />
          </button>
        </IconBarItem>
      )}
      <MyAccount />
      <IconBarItem visibleAboveBreakpoint={compact ? 'S' : undefined}>
        <a
          css={styles.link}
          href={FAVOURITES_PAGE_HREF}
          onClick={trackLinkClick(favoritesClickTrackingEvent)}
          onMouseDown={trackLinkMouseDown(favoritesClickTrackingEvent)}
        >
          {hasFavouriteProducts ? (
            <IconHeartSolid css={styles.icon} />
          ) : (
            <IconHeartOutlined css={styles.icon} />
          )}
          <IconBarItemLabel>Favourites</IconBarItemLabel>
        </a>
      </IconBarItem>
      <IconBarItem>
        <a
          css={styles.link}
          href={BASKET_PAGE_HREF}
          onClick={trackLinkClick(basketClickTrackingEvent)}
          onMouseDown={trackLinkMouseDown(basketClickTrackingEvent)}
        >
          {basketItemsCount ? (
            <>
              <IconBasketSolid css={styles.icon} />
              <BasketItemsCount count={basketItemsCount} />
            </>
          ) : (
            <IconBasketOutlined css={styles.icon} />
          )}
          <IconBarItemLabel>Basket</IconBarItemLabel>
        </a>
      </IconBarItem>
      {navigationModal && (
        <IconBarItem visibleBelowBreakpoint="L">
          <button css={styles.iconBtn} onClick={navigationModal.open} type="button">
            <IconMenu css={styles.icon} />
            <span className={ICON_BUTTON_TEXT_CLASS}>Open Navigation</span>
          </button>
        </IconBarItem>
      )}
    </ul>
  );
};
